import { useEffect, useRef } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { api } from '~portal/api';
import { getAppEnv } from '~portal/env';
import { setAccessTokenGetter } from './access-token';
import { createSocketHandlers } from './socket-handlers';
import { SocketManager } from './SocketManager';

/**
 * @desc Wires the view-independent SocketManager to the things only the React tree can reach:
 * the AuthKit token getter and the tRPC utils. Rendered once inside the ApiProvider; carries no
 * UI. All conversation state flows socket → Zustand store (see socket-handlers), regardless of
 * which route is mounted — navigating away from a chat never interrupts its stream.
 */
export function SocketBridge() {
  const trpc = api.useUtils();
  const { getAccessToken } = useAuth();

  // Refs, not deps: the manager is configured once, and resolves the CURRENT token getter /
  // utils at call time (the SDK swaps in a working getAccessToken after it initialises).
  const getAccessTokenRef = useRef(getAccessToken);
  const trpcRef = useRef(trpc);
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
    trpcRef.current = trpc;
  }, [getAccessToken, trpc]);

  useEffect(() => {
    const env = getAppEnv();
    SocketManager.configure(
      // The broker host once the wave-2 cutover sets VITE_WS_URL; the backend's own in-process
      // socket server until then.
      { url: env.wsUrl ?? env.backendApiUrl, getToken: () => getAccessTokenRef.current() },
      createSocketHandlers(() => trpcRef.current),
    );
    // Admin room joins carry their own current token (D13): the same getter, resolved per join,
    // published for the admin live client rather than imported, which keeps that client out of
    // the entry chunk.
    setAccessTokenGetter(() => getAccessTokenRef.current());
  }, []);

  return null;
}
